import React from 'react'
import { Image, Platform, ScrollView } from 'react-native'

import { env } from '@env'
import { useRoute } from '@react-navigation/native'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigation } from '@zet/common/hooks/useNavigation'
import { useWebSocket } from '@zet/common/hooks/useWebSocket'
import { Button } from '@zet/design-system/button'
import { Stack, XStack } from '@zet/design-system/layout'
import LoansCategories from '@zet/design-system/svg/LoansCategories'
import { B5, SH1 } from '@zet/design-system/typography'
import { i18n } from '@zet/localization'
import RegistrationForm from '@zet/svg/registrationForm'
import { isApp } from '@zet/utils'
import { LoggedInNavigation } from '@zet/utils/interface'
import { getName } from '@zet/utils/stringConversion'
import Lottie from 'lottie-react'
import LottieView from 'lottie-react-native'

import { useBrandDetailsById } from '../../api/brands/hooks'
import ProductCard from '../../components/productCard'
import useBrandStore from '../../store/brands'

const CrossSelling = () => {
  const queryClient = useQueryClient()
  const navigation = useNavigation<LoggedInNavigation>()

  const route = useRoute()
  const { params } = route
  const brandId = params?.brandId
  const { data: content } = useBrandDetailsById(brandId || '')
  const customerName = params?.customerName
  const notEligibleReason = params?.notEligibleReason
  const { setLeadSource } = useBrandStore((state) => state)

  const recommendationData = useWebSocket(
    `${env.WEB_SOCKET_BASE_URL}?customerId=${params?.customerId}`,
    'cross-selling-done',
  )

  return (
    <>
      <ScrollView className="bg-grays-30" showsVerticalScrollIndicator={false}>
        <Stack className="mt-1  items-center bg-white px-3 py-6">
          <RegistrationForm />
          {Boolean(content?.logo) && (
            <Image
              source={{ uri: content?.logo }}
              style={{
                width: 100,
                height: 32,
              }}
              className="my-3"
              resizeMode={'contain'}
            />
          )}
          <SH1 className="text-grays-80 text-center">
            {i18n.t('crossSelling.notEligible', {
              customerName,
            })}
          </SH1>
          <B5 className="text-grays-50 mt-1 text-center">
            {notEligibleReason + '. ' + i18n.t('crossSelling.selectBrandBelow')}
          </B5>
        </Stack>
        {!recommendationData ? (
          <Stack className="mt-4 flex-1 items-center justify-center">
            <SH1 className="px-8 text-center">
              {i18n.t('crossSelling.suitableBrands')}
            </SH1>
            {Platform.OS === 'web' ? (
              <Lottie
                animationData={require('../../assets/lottie/CrossSellingSearch.json')}
              />
            ) : (
              <LottieView
                autoPlay
                loop
                className="h-[200] w-full"
                source={require('../../assets/lottie/CrossSellingSearch.json')}
              />
            )}
          </Stack>
        ) : (
          <>
            <XStack className="border-grays-40 items-center border-b border-t bg-white px-3 py-2">
              <LoansCategories size={36} />
              <Stack className="ml-1">
                <SH1>
                  {i18n.t('crossSelling.eligibleBrandsFor', {
                    customerName,
                  })}
                </SH1>
                <B5>{i18n.t('crossSelling.brandMatch')}</B5>
              </Stack>
            </XStack>
            <Stack className="bg-white pt-3">
              <ProductCard
                partnerId={recommendationData?.eligiblePartnerId}
                onHold={false}
                customerId={params?.customerId}
                route={route}
              />
              {isApp && (
                <Stack className="bg-white px-3 pb-3">
                  <Button
                    className="w-full"
                    size="medium"
                    onPress={() => {
                      queryClient.invalidateQueries({
                        queryKey: [
                          `shareLink-${recommendationData?.eligiblePartnerId}`,
                        ],
                      })
                      setLeadSource('cross-api-agent')
                      navigation.navigate('ShareCardLink', {
                        brandId: recommendationData?.eligiblePartnerId,
                        customerName: getName(customerName),
                        customerId: params?.customerId,
                      })
                    }}
                  >
                    {i18n.t('crossSelling.share')}
                  </Button>
                </Stack>
              )}
            </Stack>
          </>
        )}
      </ScrollView>
    </>
  )
}

export default CrossSelling
